import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { allTravelDnaSteps } from '@/_constants/TravelDnaStep.ts';
import TravelDnaStep from '@/_constants/TravelDnaStep.ts';
import { allAccessibilityRequirements } from '$/_const/dna/AccessibilityRequirement.ts';
import { CONSENT_POLICY_VERSION } from '$/_const/dna/ConsentPolicyVersion.ts';
import type { TravelDnaFormData } from '$/models/Dna.d.ts';
import type { OnCompleteArgs } from '@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx';
import DnaStepLayout from '@/components/functional/TravelDnaManipulationForm/shared/DnaStepLayout.tsx';
import DnaPill from '@/components/functional/TravelDnaManipulationForm/shared/DnaPill.tsx';
import DnaPrimaryButton from '@/components/functional/TravelDnaManipulationForm/shared/DnaPrimaryButton.tsx';
import { ConsentComponent } from '@/components/functional/Consent/Consent.tsx';
import DnaError from '@/components/functional/TravelDnaManipulationForm/shared/DnaError.tsx';

const yourAccessibilitySchema = z.object({
  accessibility_requirements: z.array(
    z.enum(allAccessibilityRequirements)
  ),
  accessibility_requirements_custom: z.string().trim().max(2000).nullable(),
  userConsent: z.boolean(),
}).refine((data) => data.userConsent || (
  data.accessibility_requirements.length === 0 && !data.accessibility_requirements_custom
), {
  path: ['userConsent'],
  message: 'Please confirm that Journi may use this information when planning your trips.',
});

type YourAccessibilityForm = z.infer<typeof yourAccessibilitySchema>;
export type YourAccessibilitySchema = Omit<YourAccessibilityForm, 'userConsent'>;

type YourAccessibilityProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

function YourAccessibility({
  onComplete,
  onCompleteWhenDirty,
}: YourAccessibilityProps) {
  const { t } = useTranslation();
  const { nextStep, setContextData, contextData } = useStepperContext<
    typeof allTravelDnaSteps,
    TravelDnaFormData
  >();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const critical = contextData?.critical_requirements;

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty, errors },
  } = useForm<YourAccessibilityForm>({
    resolver: zodResolver(yourAccessibilitySchema),
    defaultValues: {
      userConsent: false,
      accessibility_requirements:
        critical?.accessibility_requirements ?? [],
      accessibility_requirements_custom:
        critical?.accessibility_requirements_custom ?? '',
    },
  });

  const selected = watch('accessibility_requirements') ?? [];
  const customValue = watch('accessibility_requirements_custom') ?? '';
  const containsInformation = selected.length > 0 || Boolean(customValue.trim());

  const complete = async (
    data: YourAccessibilitySchema,
    shouldPersist: boolean,
    userConsent = false,
    skipped = false
  ) => {
    const context: TravelDnaFormData = {
      ...contextData,
      critical_requirements: {
        ...contextData?.critical_requirements,
        ...data,
      },
    };
    const args: OnCompleteArgs = {
      step: TravelDnaStep.YOUR_ACCESSIBILITY,
      consent: {
        page: TravelDnaStep.YOUR_ACCESSIBILITY,
        version: CONSENT_POLICY_VERSION,
        status: userConsent ? 'granted' : 'withdrawn',
      },
      skipped,
      data,
      context,
    };

    if (shouldPersist) {
      setContextData(() => context);
      await onCompleteWhenDirty?.(args);
    }

    await onComplete?.(args);
    nextStep();
  };

  const onSubmit = (formData: YourAccessibilityForm) => {
    void (async () => {
      try {
        setIsPending(true);
        setError(null);
        const data: YourAccessibilitySchema = {
          accessibility_requirements: formData.accessibility_requirements,
          accessibility_requirements_custom:
            formData.accessibility_requirements_custom?.trim() || null,
        };

        await complete(data, isDirty || !critical, formData.userConsent);
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError))
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  const skip = () => {
    void (async () => {
      try {
        if (isPending) return;
        setIsPending(true);
        setError(null);
        const data: YourAccessibilitySchema = {
          accessibility_requirements:
            critical?.accessibility_requirements ?? [],
          accessibility_requirements_custom:
            critical?.accessibility_requirements_custom ?? null,
        };
        await complete(data, false, false, true);
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError))
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <DnaStepLayout
      eyebrow={t('travelDnaPage.yourAccessibility.header')}
      title={t('travelDnaPage.yourAccessibility.subheader')}
      description={t('travelDnaPage.yourAccessibility.description')}
      maxWidthClassName="max-w-[1120px]"
    >
      <form
        className="mt-6 flex w-full flex-col items-center"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <DnaError error={error} className="mb-6" />

        <h2 className="font-sans text-sm font-semibold uppercase text-[var(--gold)]">
          {t('travelDnaPage.yourAccessibility.selectionLabel')}
        </h2>

        <Controller
          control={control}
          name="accessibility_requirements"
          render={({ field }) => (
            <div className="mt-4 flex w-full flex-wrap justify-center gap-3 sm:gap-4">
              {allAccessibilityRequirements.map((option) => {
                const isSelected = field.value.includes(option);
                return (
                  <DnaPill
                    key={option}
                    label={t(
                      `travelDnaPage.yourAccessibility.options.${option}`
                    )}
                    selected={isSelected}
                    disabled={isPending}
                    onClick={() =>
                      field.onChange(
                        isSelected
                          ? field.value.filter((item) => item !== option)
                          : [...field.value, option]
                      )
                    }
                  />
                );
              })}
            </div>
          )}
        />

        <label className="mt-10 w-full max-w-[970px] text-left font-sans text-base text-[var(--cream)]">
          {t('travelDnaPage.yourAccessibility.customLabel')}{' '}
          <span className="rounded-full border border-[var(--gold-deep)] px-2 py-1 text-[10px] uppercase text-[var(--gold)]">
            {t('travelDnaPage.yourAccessibility.optional')}
          </span>
          <input
            {...register('accessibility_requirements_custom')}
            className="mt-3 h-12 w-full rounded-[9px] border border-[var(--gold-deep)] bg-[var(--gold-deep)] px-4 font-sans text-base font-semibold text-white outline-none placeholder:text-white/85 focus:border-[var(--gold-lt)]"
            placeholder={t(
              'travelDnaPage.yourAccessibility.customPlaceholder'
            )}
          />
        </label>

        <section className="mt-5 w-full max-w-[970px] rounded-[10px] border border-[var(--gold-deep)] p-4 text-left font-sans text-sm leading-relaxed text-[var(--gold)] sm:text-base">
          <p>{t('travelDnaPage.yourAccessibility.privacyNotice')}</p>
          <div className="mt-3">
            <ConsentComponent
              userConsent={watch('userConsent')}
              setConsent={(checked) => setValue('userConsent', checked, { shouldDirty: true, shouldValidate: true })}
              invalid={containsInformation && Boolean(errors.userConsent)}
              disabled={isPending}
            />
          </div>
        </section>

        <DnaPrimaryButton className="mt-6" loading={isPending} type="submit">
          {t('travelDnaPage.yourAccessibility.continue')}
        </DnaPrimaryButton>

        <button
          type="button"
          onClick={skip}
          disabled={isPending}
          className="mt-4 font-sans text-base text-[var(--cream)] underline underline-offset-4 transition-colors hover:text-[var(--gold)] disabled:opacity-50"
        >
          {t('travelDnaPage.yourAccessibility.skip')}
        </button>
      </form>
    </DnaStepLayout>
  );
}

export default YourAccessibility;
